import { action, computed, makeAutoObservable, observable } from 'mobx';
import { createIntl } from '@formatjs/intl';
import type { IntlShape } from '@formatjs/intl';

import enTranslations from 'locales/en.json';

export enum Locale {
  EN = 'en',
  PT = 'pt',
  HI = 'hi',
  ES = 'es',
}

type TranslationsMapValueType = NonNullable<Parameters<typeof createIntl>[0]['messages']>;
type TranslationsMapType = Record<Locale, TranslationsMapValueType>;

const translationsMap: TranslationsMapType = {
  [Locale.EN]: enTranslations,
  [Locale.PT]: enTranslations,
  [Locale.HI]: enTranslations,
  [Locale.ES]: enTranslations,
}

class IntlStore {
  @observable private _intl: IntlShape;
  @observable private readonly _translationsMap: TranslationsMapType;

  constructor(locale: Locale, defaultLocale: Locale, translationMaps: TranslationsMapType) {
    makeAutoObservable(this)

    this._translationsMap = translationMaps;

    this._intl = createIntl({
      locale,
      defaultLocale,
      messages: this._translationsMap[locale],
    });
  }

  @computed
  public get locale(): Locale {
    return this._intl.locale as Locale;
  }

  @computed
  public get intl() {
    return this._intl;
  }

  @computed
  public get getTranslations() {
    return this._translationsMap[this._intl.locale as Locale];
  }

  @action
  public setLocale = (locale: Locale) => {
    const defaultLocale = this._intl.defaultLocale as Locale;
    this._intl = createIntl({
      locale,
      defaultLocale,
      messages: this._translationsMap[locale],
    });
  };
}

export default new IntlStore(Locale.EN, Locale.EN, translationsMap);
